/**
 * Number, money and date formatting — port of `fmt`, `tone` and the month helpers in the
 * UI/UX prototype (UIUX/v1/js/core.js, js/entities.js). Amounts are whole euros.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MINUS = '−';
/** Word joiner: keeps a sign on the same line as its amount in narrow table columns. */
const JOIN = '\u2060';

/** Rounds half away from zero, like the backend (`MidpointRounding.AwayFromZero`); never −0. */
const round = (value: number) => Math.sign(value) * Math.round(Math.abs(value)) || 0;

/** Whole number with thousands separators; negatives with "−", never "-0". */
const grouped = (value: number) => {
  const r = round(value);
  return `${r < 0 ? MINUS : ''}${Math.abs(r).toLocaleString('en-US')}`;
};

/** Fixed-point text; a value that rounds to zero has no sign, negatives use "−". */
const fixed = (value: number, digits: number) => {
  const text = Math.abs(value).toFixed(digits);
  return value < 0 && Number(text) !== 0 ? MINUS + text : text;
};

/** "+1.2", "−0.4", or "±0.0" when the value rounds to zero at the shown digits. */
const signedFixed = (value: number, digits: number) => {
  const text = Math.abs(value).toFixed(digits);
  return `${Number(text) === 0 ? '±' : value > 0 ? '+' : MINUS}${text}`;
};

const finite = (value: number | null | undefined): value is number =>
  value != null && Number.isFinite(value);

export const fmt = {
  /**
   * €512 below €10k, €18.1k from €10k, €1.12M from €1M (absolute value). The unit is chosen after
   * rounding, so €999,950 is "€1.00M", not "€1000.0k".
   */
  eur(value: number): string {
    const a = Math.abs(value);
    if (Math.round(a / 100) * 100 >= 1e6) return `€${(a / 1e6).toFixed(2)}M`;
    if (round(a) >= 1e4) return `€${(a / 1e3).toFixed(1)}k`;
    return `€${grouped(a)}`;
  },
  /** Full value for tables: €1,250 (absolute value). */
  eurFull: (value: number) => `€${grouped(Math.abs(value))}`,
  /** With cents: €106.62 (absolute value), e.g. ARPA. */
  eurCents: (value: number) =>
    `€${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  /** +€1.2k / −€512; no sign when it rounds to €0. */
  signed(value: number, full = false): string {
    const r = round(value);
    const sign = r > 0 ? '+' : r < 0 ? MINUS : '';
    return (sign ? sign + JOIN : '') + (full ? fmt.eurFull(value) : fmt.eur(value));
  },
  pct: (value: number | null | undefined, digits = 1) =>
    finite(value) ? `${fixed(value * 100, digits)}%` : '—',
  /** "+12.5%", "−3.0%"; "±0.0%" when the change rounds to zero. */
  deltaPct: (value: number | null | undefined) =>
    finite(value) ? `${signedFixed(value * 100, 1)}%` : '—',
  /** Difference of two fractions in percentage points: "+1.8 pp", "±0.0 pp". */
  pp: (value: number | null | undefined) =>
    finite(value) ? `${signedFixed(value * 100, 1)} pp` : '—',
  num: (value: number) => grouped(value),
  /** count(1, 'subscription') → "1 subscription"; irregular plurals via `many`. */
  count: (n: number, one: string, many = `${one}s`) =>
    `${grouped(n)} ${round(n) === 1 ? one : many}`,
  /** "+3", "−2", "±0". */
  signedNum: (value: number) => {
    const r = round(value);
    return `${r > 0 ? '+' : r < 0 ? MINUS : '±'}${Math.abs(r).toLocaleString('en-US')}`;
  },
  /** A plain number with fixed digits and a signed difference, e.g. quick ratio "0.74", "−1.42". */
  decimal: (value: number, digits = 2) => (Number.isFinite(value) ? fixed(value, digits) : '—'),
  signedDecimal: (value: number, digits = 2) =>
    Number.isFinite(value) ? signedFixed(value, digits) : '—',
};

export type Tone = 'delta--up' | 'delta--down' | 'delta--flat';

/**
 * CSS modifier for a change: up is good unless `inverse`. A change smaller than `epsilon` is flat:
 * the default 0.0005 is half the last digit of a "12.3%" or "1.2 pp" delta of a fraction (and
 * below any whole euro or count), so a delta shown as "±0.0%" is never red or green.
 */
export const tone = (value: number, inverse = false, epsilon = 0.0005): Tone => {
  const p = inverse ? -value : value;
  return p >= epsilon ? 'delta--up' : p <= -epsilon ? 'delta--down' : 'delta--flat';
};

/* ---------- Months (index = months since Jan 2000) ---------- */

const BASE_YEAR = 2000;

export const monthIndex = (ym: string) =>
  (Number(ym.slice(0, 4)) - BASE_YEAR) * 12 + Number(ym.slice(5, 7)) - 1;
export const monthKey = (k: number) =>
  `${BASE_YEAR + Math.floor(k / 12)}-${String((k % 12) + 1).padStart(2, '0')}`;
/** "Oct ’26" — chart axes. */
export const monthShort = (k: number) =>
  `${MONTHS[k % 12] ?? ''} ’${String((BASE_YEAR + Math.floor(k / 12)) % 100).padStart(2, '0')}`;
/** "Oct 2026". */
export const monthLong = (k: number) => `${MONTHS[k % 12] ?? ''} ${BASE_YEAR + Math.floor(k / 12)}`;
/** "Q3 2026". */
export const quarterLabel = (k: number) =>
  `Q${Math.floor((k % 12) / 3) + 1} ${BASE_YEAR + Math.floor(k / 12)}`;

/** "14 Oct 2026" from "2026-10-14"; "—" when empty. */
export const longDate = (iso: string | null | undefined) =>
  iso
    ? `${Number(iso.slice(8, 10))} ${MONTHS[Number(iso.slice(5, 7)) - 1] ?? ''} ${iso.slice(0, 4)}`
    : '—';

/** Whole calendar days from `today` (yyyy-MM-dd) to `iso`; negative in the past. */
export const daysBetween = (today: string, iso: string) =>
  Math.round(
    (Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 864e5,
  );

/** "today", "tomorrow", "in 5 days"; "yesterday", "5 days ago" in the past. */
export const relativeDays = (days: number) =>
  days === 0
    ? 'today'
    : days === 1
      ? 'tomorrow'
      : days === -1
        ? 'yesterday'
        : days < 0
          ? `${-days} days ago`
          : `in ${days} days`;

/** Show every nth x-axis label so long ranges stay readable. */
export const axisLabel = (n: number, format: (k: number) => string = monthShort) => {
  const step = Math.ceil(n / 12);
  return (i: number, k: number) => ((n - 1 - i) % step === 0 ? format(k) : '');
};

/** "14 Oct 2026, 06:00" (UTC) for sync timestamps. */
export const syncedLabel = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return '—';
  const time = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()] ?? ''} ${d.getUTCFullYear()}, ${time}`;
};

/* ---------- Data explorer (raw SQL values) ---------- */

/**
 * Formats a native SQL `datetime` (ISO without offset) as `YYYY-MM-DD HH:mm:ss`, as stored.
 */
export function formatSqlDateTime(value: string): string {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}(?::\d{2})?)/.exec(value);
  return match ? `${match[1] ?? ''} ${match[2] ?? ''}` : value;
}

/** Formats an ISO timestamp with offset (SQL `datetimeoffset`), keeping the stored offset. */
export function formatIsoDateTimeOffset(value: string): string {
  const match =
    /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}(?::\d{2})?)(?:\.\d+)?(Z|[+-]\d{2}:\d{2})?$/i.exec(value);
  if (!match) return value;
  const offset = match[3] ? (match[3].toUpperCase() === 'Z' ? ' UTC' : ` ${match[3]}`) : '';
  return `${match[1] ?? ''} ${match[2] ?? ''}${offset}`;
}

export const formatCount = (value: number) => grouped(value);

/** Avatar initials: first and last word, or the first two letters of a single word. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters =
    parts.length > 1
      ? `${parts[0]?.[0] ?? ''}${parts.at(-1)?.[0] ?? ''}`
      : (parts[0] ?? '').slice(0, 2);
  return letters.toUpperCase();
}
